import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Chip,
  Box,
  Button,
  Pagination,
  TextField,
  InputAdornment,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  Slide,
  Tooltip,
  Divider,
  useScrollTrigger
} from '@mui/material';
import {
  Search as SearchIcon,
  CalendarToday,
  Person,
  Menu as MenuIcon,
  GitHub as GitHubIcon,
  LinkedIn as LinkedInIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Close as CloseIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { ColorModeContext } from '../App';
import Footer from '../components/Footer';
import ddosProtection from '../utils/ddosProtection';

const drawerWidth = 200;

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const Main = styled('main')(({ theme }) => ({
  flexGrow: 1,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[1],
  height: 70,
  display: 'flex',
  justifyContent: 'center',
  transition: 'all 0.3s ease',
}));

const NavButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  color: theme.palette.text.secondary,
  position: 'relative',
  fontSize: '0.85rem',
  fontWeight: 500,
  letterSpacing: '0.02em',
  textTransform: 'capitalize',
  padding: theme.spacing(0.5, 1),
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: 2,
    background: theme.palette.primary.main,
    opacity: 0,
    transform: 'scaleX(0)',
    transformOrigin: 'center',
    transition: 'transform 0.2s ease, opacity 0.2s ease',
  },
  '&:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
    '&::after': {
      opacity: 0.7,
      transform: 'scaleX(0.5)',
    },
  },
  '&.active': {
    color: theme.palette.primary.main,
    fontWeight: 600,
    '&::after': {
      opacity: 1,
      transform: 'scaleX(0.5)',
    },
  },
}));

const Logo = styled(Typography)(({ theme }) => ({
  fontFamily: '"Inter", sans-serif',
  fontWeight: 600,
  letterSpacing: '0.02em',
  fontSize: '1.1rem',
  color: theme.palette.text.primary,
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: -14,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
  },
}));

const ThemeToggleButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  borderRadius: '50%',
  marginLeft: theme.spacing(1),
  padding: theme.spacing(1),
  transition: 'all 0.2s ease',
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.05)' 
    : 'rgba(0, 0, 0, 0.03)',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.1)' 
      : 'rgba(0, 0, 0, 0.06)',
    transform: 'translateY(-2px)',
  },
}));

const ScrollTopButton = styled(IconButton)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(2),
  right: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.secondary,
  width: 32,
  height: 32,
  '&:hover': {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.secondary.main,
  },
  zIndex: 1000,
}));

const MobileDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    backgroundColor: theme.palette.background.default,
    borderLeft: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1, 0),
  }
}));

const MobileNavItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  '&:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.text.primary,
  },
  '&.active': {
    backgroundColor: 'transparent',
    color: theme.palette.secondary.main,
  },
}));

const MobileNavText = styled(ListItemText)(({ theme, active }) => ({
  '& .MuiTypography-root': {
    fontFamily: '"Inter", "Roboto", sans-serif',
    fontSize: '0.95rem',
    letterSpacing: '0.01em',
    color: active ? theme.palette.primary.main : theme.palette.text.primary,
    fontWeight: active ? 600 : 500,
    textTransform: 'none',
  },
}));

const SocialIcon = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  padding: theme.spacing(1),
  fontSize: '1rem',
  transition: 'all 0.2s ease',
  '&:hover': {
    color: theme.palette.secondary.main,
    transform: 'translateY(-2px)',
    backgroundColor: 'transparent',
  },
}));

function ScrollTop(props) {
  const { children } = props;
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const handleClick = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <Slide direction="up" in={trigger}>
      <Box
        onClick={handleClick}
        role="presentation"
        sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 2 }}
      >
        {children}
      </Box>
    </Slide>
  );
}

const BlogList = () => {
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const postsPerPage = 6;
  const navigate = useNavigate();

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  useEffect(() => {
    filterPosts();
  }, [posts, searchTerm, selectedTag]);

  const fetchBlogPosts = async () => {
    try {
      setError(''); // Clear any previous errors
      // fetchWithRetry returns response text directly, not Response object
      const responseText = await ddosProtection.fetchWithRetry('/blogs/blogs-index.json');
      const data = JSON.parse(responseText);
      setPosts(data.posts || []);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      
      // Show user-friendly error messages for rate limiting
      if (error.message.includes('Request blocked')) {
        setError('Too many requests. Please wait a moment before trying again.');
      } else if (error.message.includes('Rate limit')) {
        setError('Rate limit exceeded. Please slow down your requests.');
      } else if (error.message.includes('Request timeout')) {
        setError('Request timed out. Please check your connection and try again.');
      }else if(error.message.includes('Too Many Requests')){
        setError('Too many requests. Please wait a moment before trying again.');
      }
       else {
        setError('Failed to load blog posts. Please try again later.');
      }
      
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const filterPosts = () => {
    let filtered = posts;

    if (searchTerm) {
      filtered = filtered.filter(post =>
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (selectedTag) {
      filtered = filtered.filter(post => post.tags.includes(selectedTag));
    }

    setFilteredPosts(filtered);
    setCurrentPage(1);
  };

  const handlePostClick = (slug) => {
    navigate(`/blog/${slug}`);
  };

  const handleTagClick = (tag) => {
    setSelectedTag(selectedTag === tag ? '' : tag);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const menuItems = [
    { text: 'Home', path: '/' },
    { text: 'About', path: '/#about' },
    { text: 'Projects', path: '/#projects' },
    { text: 'Skills', path: '/#skills' },
    { text: 'Contact', path: '/#contact' },
    { text: 'Blog', path: '/blog' },
  ];

  // Pagination
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const startIndex = (currentPage - 1) * postsPerPage;
  const currentPosts = filteredPosts.slice(startIndex, startIndex + postsPerPage);

  // Get all unique tags
  const allTags = [...new Set(posts.flatMap(post => post.tags))];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <StyledAppBar position="fixed">
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between', height: '100%', minHeight: '60px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Logo onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
                <span style={{color:theme.palette.secondary.main}}>/</span>KumarAv1nash
              </Logo>
            </Box>
            
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <NavButton onClick={() => navigate('/')}>Home</NavButton>
                <NavButton onClick={() => navigate('/#about')}>About</NavButton>
                <NavButton onClick={() => navigate('/#projects')}>Projects</NavButton>
                <NavButton onClick={() => navigate('/#skills')}>Skills</NavButton>
                <NavButton onClick={() => navigate('/#contact')}>Contact</NavButton>
                <NavButton className="active" onClick={() => navigate('/blog')}>Blog</NavButton>
                <Tooltip title={theme.palette.mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
                  <ThemeToggleButton onClick={colorMode.toggleColorMode} aria-label="toggle dark/light mode">
                    {theme.palette.mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
                  </ThemeToggleButton>
                </Tooltip>
              </Box>
            )}
            
            {isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title={theme.palette.mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
                  <ThemeToggleButton onClick={colorMode.toggleColorMode} aria-label="toggle dark/light mode">
                    {theme.palette.mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
                  </ThemeToggleButton>
                </Tooltip>
                <IconButton
                  edge="end"
                  color="inherit"
                  aria-label="menu"
                  onClick={handleDrawerOpen}
                  sx={{ display: { sm: 'block', md: 'none' }, ml: 1 }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            )}
          </Toolbar>
        </Container>
      </StyledAppBar>
      
      {/* Mobile Drawer */}
      <MobileDrawer
        anchor="right"
        open={open}
        onClose={handleDrawerClose}
        variant="temporary"
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1, alignItems: 'center' }}>
          <Logo>Avinash Kumar</Logo>
          <IconButton onClick={handleDrawerClose} sx={{ color: 'text.secondary', padding: 0 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider sx={{ my: 1, borderColor: 'divider' }} />
        <List sx={{ mb: 'auto' }}>
          {menuItems.map((item, index) => (
            <MobileNavItem
              button
              key={item.text}
              onClick={() => {
                navigate(item.path);
                handleDrawerClose();
              }}
              className={item.text === 'Blog' ? 'active' : ''}
            >
              <MobileNavText 
                primary={item.text}
                active={item.text === 'Blog'}
              />
            </MobileNavItem>
          ))}
        </List>
        <Divider sx={{ my: 1, borderColor: 'divider' }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
          <Box sx={{ display: 'flex' }}>
            <Tooltip title="GitHub">
              <SocialIcon size="small" href="https://github.com/kumarav1nash" target="_blank">
                <GitHubIcon sx={{ fontSize: '1rem' }} />
              </SocialIcon>
            </Tooltip>
            <Tooltip title="LinkedIn">
              <SocialIcon size="small" href="https://www.linkedin.com/in/kumarav1nash/" target="_blank">
                <LinkedInIcon sx={{ fontSize: '1rem' }} />
              </SocialIcon>
            </Tooltip>
          </Box>
        </Box>
      </MobileDrawer>
      
      {/* Main Content */}
      <Main>
        <Toolbar sx={{ minHeight: '60px !important' }} />
        <Container maxWidth="lg" sx={{ py: 6, flexGrow: 1 }}>
          {/* Page Header */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" component="h1" gutterBottom>
              Blog
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              Thoughts, tutorials, and insights about web development, technology, and more.
            </Typography>
          </Box>

          {/* Search and Filters */}
          <Box sx={{ mb: 4 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search posts..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />

            {/* Tag Filter */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip
                label="All"
                variant={selectedTag === '' ? 'filled' : 'outlined'}
                onClick={() => setSelectedTag('')}
                sx={{ cursor: 'pointer' }}
              />
              {allTags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  variant={selectedTag === tag ? 'filled' : 'outlined'}
                  onClick={() => handleTagClick(tag)}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>

          {/* Loading and Error States */}
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                Loading posts...
              </Typography>
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="error" gutterBottom>
                {error}
              </Typography>
              <Button 
                variant="contained" 
                onClick={() => {
                  setError('');
                  setLoading(true);
                  fetchBlogPosts();
                }}
                sx={{ mt: 2 }}
              >
                Try Again
              </Button>
            </Box>
          ) : (
            <>
              {/* Posts Grid */}
              <Grid container spacing={4} sx={{ mb: 6 }}>
                {currentPosts.map((post) => (
                  <Grid item xs={12} md={6} lg={4} key={post.slug}>
                    <StyledCard>
                      {post.image && (
                        <CardMedia
                          component="img"
                          height="200"
                          image={post.image}
                          alt={post.title}
                        />
                      )}
                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="h6" component="h2" gutterBottom>
                          {post.title}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarToday fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {new Date(post.date).toLocaleDateString()}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Person fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {post.author}
                            </Typography>
                          </Box>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                          {post.excerpt}
                        </Typography>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                          {post.tags.map((tag) => (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              variant="outlined"
                              onClick={() => handleTagClick(tag)}
                              sx={{ cursor: 'pointer' }}
                            />
                          ))}
                        </Box>

                        <Button
                          variant="contained"
                          onClick={() => handlePostClick(post.slug)}
                          sx={{ mt: 'auto' }}
                        >
                          Read More
                        </Button>
                      </CardContent>
                    </StyledCard>
                  </Grid>
                ))}
              </Grid>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(event, value) => setCurrentPage(value)}
                    color="primary"
                    size="large"
                  />
                </Box>
              )}
            </>
          )}
        </Container>
        
        {/* Footer */}
        <Footer />
      </Main>
      
      {/* Scroll to Top */}
      <ScrollTop>
        <ScrollTopButton size="small">
          <KeyboardArrowUpIcon fontSize="small" />
        </ScrollTopButton>
      </ScrollTop>
    </Box>
  );
};

export default BlogList;